/**
 * InboxCredentialsFields - Campos dinâmicos de credenciais do provedor
 *
 * Renderiza os campos definidos no schema de credenciais da versão/provider,
 * incluindo o tratamento especial de `webhook_verify_token` (geração segura)
 * e o alerta de conflito de `instance_id`.
 *
 * RELEASE: 2026-08-08 v1.0.0 - Extraído do CreateInboxDialog (fim do wizard).
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/** Campo de credencial já normalizado a partir do schema */
export interface CredentialField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  description?: string;
  defaultValue?: string;
}

interface InboxCredentialsFieldsProps {
  fields: CredentialField[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  /** Nome da inbox em conflito com o instance_id digitado (se houver) */
  instanceIdConflict: string | null;
  /** Indica verificação de conflito em andamento */
  checkingInstanceId: boolean;
  disabled?: boolean;
}

/**
 * Gera um token aleatório seguro para verificação de webhook.
 */
function generateVerifyToken(length = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let token = '';
  for (let i = 0; i < length; i++) token += chars[bytes[i] % chars.length];
  return token;
}

export function InboxCredentialsFields({
  fields,
  values,
  onChange,
  instanceIdConflict,
  checkingInstanceId,
  disabled,
}: InboxCredentialsFieldsProps) {
  if (fields.length === 0) return null;

  return (
    <>
      {fields.map((field) => (
        <div key={field.key} className="space-y-2">
          <Label htmlFor={field.key}>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>

          {field.key === 'webhook_verify_token' ? (
            <div className="flex gap-2">
              <Input
                id={field.key}
                type="text"
                value={values[field.key] || ''}
                onChange={(e) => onChange({ ...values, [field.key]: e.target.value })}
                placeholder={field.placeholder}
                disabled={disabled}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={disabled}
                onClick={() => {
                  onChange({ ...values, [field.key]: generateVerifyToken() });
                  toast.success('Token de verificação gerado!');
                }}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Gerar
              </Button>
            </div>
          ) : (
            <Input
              id={field.key}
              type={field.type === 'password' ? 'password' : 'text'}
              value={values[field.key] || ''}
              onChange={(e) => onChange({ ...values, [field.key]: e.target.value })}
              placeholder={field.placeholder}
              className={field.key === 'instance_id' && instanceIdConflict ? 'border-destructive' : ''}
              disabled={disabled}
            />
          )}

          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}

          {field.key === 'instance_id' && instanceIdConflict && (
            <div className="flex items-start gap-2 p-2 rounded-md bg-destructive/10 border border-destructive/20">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <p className="text-xs text-destructive">
                A caixa de entrada "<strong>{instanceIdConflict}</strong>" já está usando esta instância.
                Desative ou desconecte "{instanceIdConflict}" antes de continuar.
              </p>
            </div>
          )}

          {field.key === 'instance_id' && checkingInstanceId && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Verificando disponibilidade...
            </p>
          )}
        </div>
      ))}
    </>
  );
}
